import { AlertTriangle, KeyRound, Lock, Users, UserX } from "lucide-react";
import anthmaiLogo from "@/assets/anthmai-icon.svg";

type BlockReason = "PLATFORM_SUSPENDED" | "LICENSE_EXPIRED" | "SEAT_LIMIT_REACHED" | "USER_DEACTIVATED";

interface PlatformBlockedProps {
  reason: BlockReason;
  message?: string;
}

const reasonConfig: Record<BlockReason, { icon: typeof Lock; title: string; defaultMessage: string }> = {
  PLATFORM_SUSPENDED: {
    icon: Lock,
    title: "Platform Suspended",
    defaultMessage: "This platform has been temporarily suspended. Please contact your administrator.",
  },
  LICENSE_EXPIRED: {
    icon: KeyRound,
    title: "License Expired",
    defaultMessage: "Your organization's license has expired. Please contact your administrator to renew.",
  },
  SEAT_LIMIT_REACHED: {
    icon: Users,
    title: "Seat Limit Reached",
    defaultMessage: "Your organization has reached its licensed seat limit. Please contact your administrator.",
  },
  USER_DEACTIVATED: {
    icon: UserX,
    title: "Account Deactivated",
    defaultMessage: "Your account has been deactivated. Please contact your administrator to regain access.",
  },
};

export function PlatformBlocked({ reason, message }: PlatformBlockedProps) {
  const config = reasonConfig[reason] ?? reasonConfig.PLATFORM_SUSPENDED;
  const Icon = config.icon;

  return (
    <div className="flex h-screen items-center justify-center bg-surface-base">
      <div className="text-center max-w-md px-6">
        <img src={anthmaiLogo} alt="AnthmAI" className="w-12 h-12 mx-auto mb-6 opacity-50" />

        <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
          <Icon className="h-8 w-8 text-red-400" />
        </div>

        <h1 className="text-2xl font-bold text-content-primary">{config.title}</h1>

        <p className="mt-3 text-content-tertiary leading-relaxed">{message || config.defaultMessage}</p>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-content-tertiary">
          <AlertTriangle className="h-3 w-3" />
          <span>If you believe this is an error, please contact support.</span>
        </div>
      </div>
    </div>
  );
}
